import { Classes, Collapse, Drawer, DrawerProps, Position } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import { StartPointProps } from '@moleculeone/types';
import { Button, Icon, ReactionPreview, styled } from '@moleculeone/ui';
import cx from 'classnames';
import { useState } from 'react';

import { apiQueryClient } from '@f/api-client';
import LoadingScreen from '@f/components/shared/loading-screen/LoadingScreen';
import Select from '@f/components/shared/select/Select';

import cls from './ReactionConditionsSidebar.module.scss';
import { ReagentTag } from './conditions/ReagentTag';
import { ReferenceReaction, YieldReferences } from './conditions/YieldReferences';
import { YieldPrediction, YieldRange } from './conditions/Yields';
import { conditionGroupsMeta, getConditionGroupValues } from './conditions/conditionGroupsMeta';
import { ConditionGroup, REACTION_CLASSES, ReactionClassName } from './conditions/reactionClasses';

const ReactionContainer = styled('div', {
  padding: '1rem 0 2rem',
  borderBottom: '1px solid $slate5',
});

const Recommendation = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const References = styled('div', {
  padding: '$3 $5',
  fontSize: '$1',
  color: '$slate11',
});

const ReferencesTitle = styled('div', {
  fontSize: '$2',
});

const ReferencesContent = styled('div', {});

const Options = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  padding: '$3 $6',
});

const ConditionGroupSelector = styled('div', {
  maxHeight: '50vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '$6',
});

const ConditionGroupReagents = styled('div', {
  display: 'flex',
});

const ConditionGroupNumber = styled('div', {
  fontSize: '$4',
  fontWeight: 500,
  color: '$slate8',
});

const ConditionGroupContainer = styled('div', {
  'display': 'flex',
  'alignItems': 'center',
  'gap': '$4',
  'padding': '$3 $4',
  'borderBottom': '1px solid $slate5',
  'cursor': 'pointer',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: '$slate3',

    [`&:hover ${ConditionGroupNumber}`]: {
      color: '$purple9',
    },
  },
  'variants': {
    active: {
      true: {
        backgroundColor: '$slate3',
        [`${ConditionGroupNumber}`]: {
          color: '$purple9',
        },
      },
    },
  },
});

const ActiveConditionGroupReagentsContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
});

const ActiveConditionGroupReagents = styled('div', {
  'display': 'flex',
  'flexWrap': 'wrap',
  'alignItems': 'center',
  'padding': '$2 $3',
  'borderRadius': '$small',
  'backgroundColor': 'white',
  'border': '1px solid $slate7',
  '&:hover': {
    backgroundColor: '$slate3',
  },
});

export interface ReactionConditionsSidebarProps extends DrawerProps {
  reactionData: StartPointProps['reactionData'] | null;
}

export function ReactionConditionsSidebar(props: ReactionConditionsSidebarProps) {
  const { reactionData, ...restProps } = props;

  const [selectedReactionClass, setSelectedReactionClass] = useState<ReactionClassName | null>(
    null
  );
  const [conditionGroupIndex, setConditionGroupIndex] = useState<number | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const reactionSmiles = `${reactionData?.substrateSmiles.join('.')}>>${
    reactionData?.productSmiles
  }`;

  const classifyReactionQueryKey = ['reactionConditions', reactionSmiles];
  // @ts-ignore
  const { data: reactionClassesData } = apiQueryClient.reactionConditions.classifyReaction.useQuery(
    classifyReactionQueryKey,
    {
      query: {
        reactionSmiles,
      },
    },
    {
      queryKey: classifyReactionQueryKey,
      staleTime: Infinity,
      enabled: reactionData != null,
    }
  );
  const reactionClasses = reactionClassesData?.body ?? [];

  const reactionClass =
    selectedReactionClass ?? (reactionClasses[0] as ReactionClassName | undefined) ?? null;

  const conditionGroups: ConditionGroup[] = reactionClass
    ? REACTION_CLASSES[reactionClass].conditions
    : [];

  const conditionsQueryKey = [
    'reactionConditions',
    reactionSmiles,
    reactionClass,
    conditionGroupIndex,
  ];
  // @ts-ignore
  const { data: conditionsData, isFetching: isFindingConditions } =
    apiQueryClient.reactionConditions.findConditions.useQuery(
      conditionsQueryKey,
      {
        query: {
          reactionSmiles,
          reactionClass: reactionClass ?? '',
          reagents: conditionGroupIndex
            ? getConditionGroupValues(conditionGroups[conditionGroupIndex])
            : {},
        },
      },
      {
        queryKey: conditionsQueryKey,
        staleTime: Infinity,
        enabled: reactionData != null && reactionClass != null,
      }
    );

  const [openRecommendationIndex, setOpenRecommendationIndex] = useState<number | null>(null);

  const conditions = conditionsData?.body?.conditions ?? [];

  return (
    <Drawer isCloseButtonShown title="Condition recommendations" size="920px" {...restProps}>
      <div className={cx(Classes.DRAWER_BODY, cls.body)}>
        {reactionData && (
          <ReactionContainer>
            <ReactionPreview
              reaction={{
                product: reactionData.productSmiles,
                substrates: reactionData.substrateSmiles,
                productMap: reactionData.productToMap,
                substratesMap: reactionData.substratesToMap,
              }}
            />
          </ReactionContainer>
        )}

        <Options>
          <Select
            large
            outlined
            items={reactionClasses.map(rc => ({
              value: rc,
              label: REACTION_CLASSES[rc]?.name ?? rc,
            }))}
            activeItemId={reactionClass}
            idField="value"
            labelField="label"
            filterable={false}
            rightIcon="caret-down"
            onChange={selectedReactionClass => {
              setSelectedReactionClass(selectedReactionClass.value);
              setConditionGroupIndex(null);
            }}
            allowNoSelection={false}
          />

          <Popover2
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            placement={Position.BOTTOM}
            content={
              <ConditionGroupSelector>
                {conditionGroups.map((group, index) => (
                  <ConditionGroupContainer
                    key={index}
                    onClick={() => {
                      if (conditionGroupIndex === index) {
                        setConditionGroupIndex(null);
                      } else {
                        setConditionGroupIndex(index);
                      }
                      setIsPopupOpen(false);
                    }}
                    active={conditionGroupIndex === index}
                  >
                    <ConditionGroupNumber>{index + 1}</ConditionGroupNumber>
                    <ConditionGroupReagents>
                      {Object.entries(group).map(([key, value]) => (
                        <ReagentTag
                          key={key}
                          fullWidth
                          condition={key}
                          {...(conditionGroupsMeta[key].unit
                            ? { text: `${value}${conditionGroupsMeta[key].unit}` }
                            : { text: value.full_name, smiles: value.smiles })}
                        />
                      ))}
                    </ConditionGroupReagents>
                  </ConditionGroupContainer>
                ))}
              </ConditionGroupSelector>
            }
          >
            {conditionGroupIndex === null ? (
              <Button
                weight="minimal"
                color="slate"
                icon="add"
                text="Specify conditions"
                onClick={() => setIsPopupOpen(true)}
              />
            ) : (
              <ActiveConditionGroupReagentsContainer>
                <ActiveConditionGroupReagents onClick={() => setIsPopupOpen(true)}>
                  {Object.entries(conditionGroups[conditionGroupIndex]).map(([key, value]) => (
                    <ReagentTag
                      key={key}
                      fullWidth
                      condition={key}
                      {...(conditionGroupsMeta[key].unit
                        ? { text: `${value}${conditionGroupsMeta[key].unit}` }
                        : { text: value.full_name, smiles: value.smiles })}
                    />
                  ))}
                  <Icon icon="arrow_drop_down" css={{ color: '$slate11' }} />
                </ActiveConditionGroupReagents>
                <Button
                  weight="minimal"
                  color="slate"
                  icon="clear"
                  onClick={() => setConditionGroupIndex(null)}
                />
              </ActiveConditionGroupReagentsContainer>
            )}
          </Popover2>
        </Options>

        {isFindingConditions && <LoadingScreen />}

        {conditions.map((recommendation, index) => (
          <Recommendation key={index}>
            <YieldPrediction
              active={openRecommendationIndex === index}
              onClick={() => setOpenRecommendationIndex(prev => (prev === index ? null : index))}
              prediction={recommendation.predictions as YieldRange[]}
              certainty={recommendation.certainty}
              reagents={recommendation.reagents}
            />
            <Collapse isOpen={openRecommendationIndex === index}>
              <References>
                <ReferencesTitle>References</ReferencesTitle>
                <ReferencesContent>
                  <YieldReferences references={recommendation.references as ReferenceReaction[]} />
                </ReferencesContent>
              </References>
            </Collapse>
          </Recommendation>
        ))}
      </div>
    </Drawer>
  );
}
